import { formatPeriodRange, lastDayBefore } from '../../lib/period';
import type { RouterOutputs } from '../../lib/trpc';

export type CostHistory = RouterOutputs['recurringBill']['historyAll'];
export type CostHistoryBill = CostHistory['bills'][number];
export type CostHistoryPeriod = CostHistory['periods'][number];

export const RANGE_OPTIONS = [6, 12, 24] as const;
export type RangeOption = (typeof RANGE_OPTIONS)[number];

const SHORT_MONTHS = [
  'jan',
  'feb',
  'mar',
  'apr',
  'maj',
  'jun',
  'jul',
  'aug',
  'sep',
  'okt',
  'nov',
  'dec',
];

/** "okt 2026", the same period label Insikter uses */
export function periodLabel(year: number, month: number) {
  return `${SHORT_MONTHS[month - 1] ?? ''} ${year}`;
}

/** "okt", for chart ticks on narrow screens */
export function periodShortLabel(month: number) {
  return SHORT_MONTHS[month - 1] ?? '';
}

export const periodKey = (p: { year: number; month: number }) => `${p.year}-${p.month}`;

/** "20 sep – 19 okt" for a period from historyAll */
export function periodRangeLabel(period: CostHistoryPeriod) {
  return formatPeriodRange(period.startDate, lastDayBefore(period.endDate));
}

/** Öre → whole kronor for chart values */
export const toKr = (ore: number) => Math.round(ore / 100);

/** Ticks for the period axis: every period on wide screens, about six on phones */
export function tickInterval(count: number, isSmUp: boolean) {
  const target = isSmUp ? 12 : 6;
  return count <= target ? 0 : Math.ceil(count / target) - 1;
}

export type Trend = {
  label: string;
  tone: 'up' | 'down' | 'flat';
};

/** "+8 % mot snitt"; within ±3 % counts as level with the average */
export function trendVsAverage(change: number | null): Trend | null {
  if (change == null || !Number.isFinite(change)) return null;
  const pct = Math.round(change * 100);
  if (Math.abs(pct) < 3) return { label: 'I nivå med snitt', tone: 'flat' };
  return {
    label: `${pct > 0 ? '+' : '−'}${Math.abs(pct)} % mot snitt`,
    tone: pct > 0 ? 'up' : 'down',
  };
}
